/**
 * Lifecycle notification utility — creates in-app (bell) notifications
 * for FHIR resource events (message sent, task assigned,
 * appointment booked, etc.).
 *
 * Uses the same Communication shape as the Rules Engine so
 * notifications appear in the bell popover. Lifecycle emails are sent by
 * bots (notification-lifecycle-bot) and the Rules Engine, never from the UI.
 */
import type { MedplumClient } from '@medplum/core';
import type { Communication, Reference, Task } from '@medplum/fhirtypes';
import { resolveTaskNotificationCategory } from './notificationDeliveryGuards';

import { NOTIF } from './notificationFhir';
import {
  type NotificationPreferenceCategory,
  type NotificationChannel,
  CHANNEL_AVAILABILITY,
  isNotificationEnabled,
} from './notificationPreferences';
import type { ProfileRoleCode } from '@unify/fhir-model/constants';
import { getRole } from '@unify/fhir-model/resources/practitioner';
import { getPatientRole } from '@unify/fhir-model/resources/patient';
import type { Patient, Practitioner } from '@medplum/fhirtypes';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LIFECYCLE_REASON_SYSTEM = 'https://unify.health/fhir/CodeSystem/notification-lifecycle';
const LIFECYCLE_REASON_CODE = 'lifecycle-notification';

const DEDUPE_SYSTEM = 'https://unify.health/fhir/lifecycle-notification-dedupe';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface LifecycleNotificationParams {
  category: NotificationPreferenceCategory;
  recipientRef: string;
  title: string;
  body: string;
  linkTarget?: string;
  aboutRefs?: string[];
  subjectRef?: string;
  /** Skip in-app even if prefs allow it (e.g. messages — handled by Messages inbox). */
  skipInApp?: boolean;
  /** CareTeam compartment refs so providers can create/read the Communication. */
  accountRefs?: Reference[];
  /** Override dedupe time bucket (default: hourly). Use daily ISO date for digest notifications. */
  dedupeBucket?: string;
}

export interface LifecycleNotificationResult {
  inAppCreated: boolean;
}

/**
 * Create an in-app bell notification for a single recipient, respecting their
 * notification preferences and channel availability.
 *
 * Failures are logged but do not throw — notification delivery is
 * best-effort and must not block the triggering action.
 */
export async function createLifecycleNotification(
  medplum: MedplumClient,
  params: LifecycleNotificationParams,
): Promise<LifecycleNotificationResult> {
  const result: LifecycleNotificationResult = { inAppCreated: false };

  const availability = CHANNEL_AVAILABILITY[params.category];
  if (!availability) return result;

  const profileData = await loadRecipientProfile(medplum, params.recipientRef);

  const shouldInApp =
    !params.skipInApp &&
    availability.inApp !== 'coming-soon' &&
    isChannelEnabled(profileData, params.category, 'in-app', availability.inApp === 'locked-on');

  const dedupeKey = buildDedupeKey(params);

  if (shouldInApp) {
    const existing = await checkDedupe(medplum, dedupeKey);
    if (!existing) {
      try {
        await createInAppCommunication(medplum, params, dedupeKey);
        result.inAppCreated = true;
      } catch (err) {
        console.warn('[notificationLifecycle] in-app creation failed:', err);
      }
    }
  }

  return result;
}

/**
 * Send lifecycle notifications to multiple recipients (fire-and-forget).
 * Errors are silently caught per-recipient.
 */
export async function createLifecycleNotifications(
  medplum: MedplumClient,
  recipientRefs: string[],
  params: Omit<LifecycleNotificationParams, 'recipientRef'>,
): Promise<void> {
  await Promise.allSettled(
    recipientRefs.map((ref) =>
      createLifecycleNotification(medplum, { ...params, recipientRef: ref }),
    ),
  );
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

interface ProfileData {
  profile: Patient | Practitioner;
  role: ProfileRoleCode;
}

async function loadRecipientProfile(
  medplum: MedplumClient,
  recipientRef: string,
): Promise<ProfileData | null> {
  const [rt, id] = recipientRef.split('/');
  if (!rt || !id || (rt !== 'Patient' && rt !== 'Practitioner')) return null;
  try {
    const resource = await medplum.readResource(rt as 'Patient' | 'Practitioner', id);
    if (resource.resourceType === 'Patient') {
      const r = getPatientRole(resource as Patient);
      return { profile: resource as Patient, role: isValidRole(r) ? r : 'client' };
    }
    const r = getRole(resource as Practitioner);
    return { profile: resource as Practitioner, role: isValidRole(r ?? '') ? (r as ProfileRoleCode) : 'provider' };
  } catch {
    return null;
  }
}

function isValidRole(role: string): role is ProfileRoleCode {
  return [
    'unify-admin', 'practice-owner', 'practice-admin',
    'supervisor', 'provider', 'client',
  ].includes(role);
}

function isChannelEnabled(
  profileData: ProfileData | null,
  category: NotificationPreferenceCategory,
  channel: NotificationChannel,
  forcedOn: boolean,
): boolean {
  if (forcedOn) return true;
  if (!profileData) return true;
  return isNotificationEnabled(profileData.profile, profileData.role, category, channel);
}

function buildDedupeKey(params: LifecycleNotificationParams): string {
  const timeBucket = params.dedupeBucket ?? new Date().toISOString().slice(0, 13);
  return [
    'lifecycle',
    params.category,
    params.recipientRef,
    params.linkTarget ?? 'none',
    timeBucket,
  ].join('|');
}

async function checkDedupe(medplum: MedplumClient, key: string): Promise<boolean> {
  try {
    const tagCode = `in-app|${key}`;
    const results = await medplum.searchResources('Communication', {
      _tag: `${DEDUPE_SYSTEM}|${tagCode}`,
      _count: '1',
    });
    return results.length > 0;
  } catch {
    return false;
  }
}

async function createInAppCommunication(
  medplum: MedplumClient,
  params: LifecycleNotificationParams,
  dedupeKey: string,
): Promise<Communication> {
  const aboutReferences: Reference[] = (params.aboutRefs ?? []).map((ref) => ({ reference: ref }));

  const extensions: Communication['extension'] = [];
  if (params.linkTarget) {
    extensions.push({
      url: NOTIF.LINK_TARGET_EXT,
      valueReference: { reference: params.linkTarget },
    });
  }

  // Route WS events to the Patient's compartment (same pattern as messaging messages).
  // Without this, Medplum silently drops the event when a Practitioner creates the Communication.
  const patientAccount = params.recipientRef.startsWith('Patient/')
    ? { account: { reference: params.recipientRef } }
    : {};

  return medplum.createResource<Communication>({
    resourceType: 'Communication',
    status: 'in-progress',
    category: [
      {
        coding: [{ system: NOTIF.CATEGORY_SYSTEM, code: params.category }],
      },
    ],
    medium: [
      {
        coding: [{ system: NOTIF.MEDIUM_SYSTEM, code: NOTIF.MEDIUM_CODE, display: 'In-app notification' }],
      },
    ],
    reasonCode: [
      {
        coding: [{ system: LIFECYCLE_REASON_SYSTEM, code: LIFECYCLE_REASON_CODE }],
      },
    ],
    subject: params.subjectRef ? { reference: params.subjectRef } : undefined,
    recipient: [{ reference: params.recipientRef }],
    topic: { text: params.title },
    payload: [{ contentString: params.body }],
    sent: new Date().toISOString(),
    about: aboutReferences.length > 0 ? aboutReferences : undefined,
    extension: extensions.length > 0 ? extensions : undefined,
    meta: {
      ...patientAccount,
      tag: [
        { system: LIFECYCLE_REASON_SYSTEM, code: LIFECYCLE_REASON_CODE },
        { system: DEDUPE_SYSTEM, code: `in-app|${dedupeKey}` },
      ],
      ...(params.accountRefs?.length ? { accounts: params.accountRefs } : {}),
    },
  });
}

/** In-app-only client notification when a provider shares a chart file to the portal. */
export async function notifyClientDocumentShared(
  medplum: MedplumClient,
  params: {
    patientId: string;
    documentReferenceId: string;
    documentName: string;
    accountRefs?: Reference[];
  },
): Promise<LifecycleNotificationResult> {
  return createLifecycleNotification(medplum, {
    category: 'intake',
    recipientRef: `Patient/${params.patientId}`,
    title: 'New shared document',
    body: `${params.documentName} is available in your Documents.`,
    linkTarget: `DocumentReference/${params.documentReferenceId}`,
    aboutRefs: [`DocumentReference/${params.documentReferenceId}`],
    subjectRef: `Patient/${params.patientId}`,
    accountRefs: params.accountRefs,
  });
}

/** Notify a practitioner when a task is assigned or reassigned to them. */
export async function notifyTaskAssignee(
  medplum: MedplumClient,
  task: Task,
  options?: { title?: string; body?: string },
): Promise<LifecycleNotificationResult> {
  const assigneeRef = task.owner?.reference;
  if (!assigneeRef?.startsWith('Practitioner/')) {
    return { inAppCreated: false };
  }

  const category = resolveTaskNotificationCategory(task);
  return createLifecycleNotification(medplum, {
    category,
    recipientRef: assigneeRef,
    title: options?.title ?? 'Task assigned',
    body: options?.body ?? task.description ?? 'You have a new task.',
    linkTarget: task.id ? `Task/${task.id}` : undefined,
    aboutRefs: task.id ? [`Task/${task.id}`] : undefined,
    subjectRef: task.for?.reference,
  });
}

/** Notify care team when a form or file is uploaded to a client chart. */
export async function notifyFormUploaded(
  medplum: MedplumClient,
  params: {
    recipientRef: string;
    patientId: string;
    documentReferenceId: string;
    documentName: string;
    accountRefs?: Reference[];
  },
): Promise<LifecycleNotificationResult> {
  return createLifecycleNotification(medplum, {
    category: 'forms-uploaded',
    recipientRef: params.recipientRef,
    title: 'Form or file uploaded',
    body: `${params.documentName} was added to the client chart.`,
    linkTarget: `DocumentReference/${params.documentReferenceId}`,
    aboutRefs: [`DocumentReference/${params.documentReferenceId}`, `Patient/${params.patientId}`],
    subjectRef: `Patient/${params.patientId}`,
    accountRefs: params.accountRefs,
  });
}

/** Notify care team when a client submits a completed form. */
export async function notifyFormSubmitted(
  medplum: MedplumClient,
  params: {
    recipientRef: string;
    patientId: string;
    taskId: string;
    formName: string;
    accountRefs?: Reference[];
  },
): Promise<LifecycleNotificationResult> {
  return createLifecycleNotification(medplum, {
    category: 'forms-submitted',
    recipientRef: params.recipientRef,
    title: 'Form submitted',
    body: `A client submitted ${params.formName}.`,
    linkTarget: `Task/${params.taskId}`,
    aboutRefs: [`Task/${params.taskId}`, `Patient/${params.patientId}`],
    subjectRef: `Patient/${params.patientId}`,
    accountRefs: params.accountRefs,
  });
}

/** Daily in-app digest for supervisors: count of notes awaiting cosign/review. */
export async function notifySupervisionNotesDue(
  medplum: MedplumClient,
  params: {
    supervisorRef: string;
    notesDueCount: number;
    accountRefs?: Reference[];
  },
): Promise<LifecycleNotificationResult> {
  const count = params.notesDueCount;
  const noun = count === 1 ? 'note' : 'notes';
  return createLifecycleNotification(medplum, {
    category: 'supervision-notes-due',
    recipientRef: params.supervisorRef,
    title: 'Notes due for review',
    body: `You have ${count} ${noun} awaiting your review.`,
    accountRefs: params.accountRefs,
    // Intentional UTC civil day: idempotency bucket must match the server
    // supervision-notes-due cron's dayBucket, which is also UTC-based.
    // eslint-disable-next-line no-restricted-syntax
    dedupeBucket: new Date().toISOString().slice(0, 10),
  });
}

/** In-app-only client notification for a pending form Task (provider UI). */
export async function notifyClientFormTask(
  medplum: MedplumClient,
  params: {
    patientId: string;
    taskId: string;
    formName: string;
    title: string;
    body?: string;
    accountRefs?: Reference[];
  },
): Promise<LifecycleNotificationResult> {
  return createLifecycleNotification(medplum, {
    category: 'intake',
    recipientRef: `Patient/${params.patientId}`,
    title: params.title,
    body: params.body ?? `Please complete ${params.formName}.`,
    linkTarget: `Task/${params.taskId}`,
    aboutRefs: [`Task/${params.taskId}`],
    subjectRef: `Patient/${params.patientId}`,
    accountRefs: params.accountRefs,
  });
}

/** Bell notification when a provider accepts or declines a portal appointment request. */
export async function notifyClientAppointmentRequestDecision(
  medplum: MedplumClient,
  params: {
    patientId: string;
    decision: 'accept' | 'deny';
    preferredWhen?: string;
    threadId?: string;
    requestCommunicationId?: string;
    proposedAppointmentId?: string;
  },
): Promise<LifecycleNotificationResult> {
  const accepted = params.decision === 'accept';
  const linkTarget = params.threadId
    ? `Communication/${params.threadId}`
    : params.proposedAppointmentId
      ? `Appointment/${params.proposedAppointmentId}`
      : undefined;

  return createLifecycleNotification(medplum, {
    category: 'appointments',
    recipientRef: `Patient/${params.patientId}`,
    title: accepted ? 'Appointment confirmed' : 'Appointment request declined',
    body: accepted
      ? params.preferredWhen
        ? `Your appointment is confirmed for ${params.preferredWhen}.`
        : 'Your provider confirmed your appointment request.'
      : 'Your provider declined the requested time. Open Messages to pick another slot.',
    linkTarget,
    aboutRefs: [
      ...(params.requestCommunicationId ? [`Communication/${params.requestCommunicationId}`] : []),
      ...(params.proposedAppointmentId ? [`Appointment/${params.proposedAppointmentId}`] : []),
    ],
    subjectRef: `Patient/${params.patientId}`,
    dedupeBucket: `${params.requestCommunicationId ?? params.proposedAppointmentId ?? 'appointment-request'}:${params.decision}`,
  });
}

/** Bell notification when a provider accepts or declines a portal reschedule request. */
export async function notifyClientRescheduleDecision(
  medplum: MedplumClient,
  params: {
    patientId: string;
    decision: 'accept' | 'deny';
    preferredWhen?: string;
    threadId?: string;
    requestCommunicationId?: string;
    appointmentId?: string;
  },
): Promise<LifecycleNotificationResult> {
  const accepted = params.decision === 'accept';
  const linkTarget = params.threadId
    ? `Communication/${params.threadId}`
    : params.appointmentId
      ? `Appointment/${params.appointmentId}`
      : undefined;

  return createLifecycleNotification(medplum, {
    category: 'appointments',
    recipientRef: `Patient/${params.patientId}`,
    title: accepted ? 'Reschedule accepted' : 'Reschedule declined',
    body: accepted
      ? params.preferredWhen
        ? `Your appointment is confirmed for ${params.preferredWhen}.`
        : 'Your provider accepted your new appointment time.'
      : 'Your provider declined the requested time. Open Messages to find another slot.',
    linkTarget,
    aboutRefs: [
      ...(params.requestCommunicationId ? [`Communication/${params.requestCommunicationId}`] : []),
      ...(params.appointmentId ? [`Appointment/${params.appointmentId}`] : []),
    ],
    subjectRef: `Patient/${params.patientId}`,
    dedupeBucket: `${params.requestCommunicationId ?? params.appointmentId ?? 'reschedule'}:${params.decision}`,
  });
}

/** Bell notification when a provider moves a booked appointment from the calendar. */
export async function notifyClientAppointmentTimeChanged(
  medplum: MedplumClient,
  params: {
    patientId: string;
    appointmentId: string;
    whenLabel: string;
    previousWhenLabel?: string;
  },
): Promise<LifecycleNotificationResult> {
  const body = params.previousWhenLabel
    ? `Your appointment moved from ${params.previousWhenLabel} to ${params.whenLabel}.`
    : `Your appointment is now scheduled for ${params.whenLabel}.`;

  return createLifecycleNotification(medplum, {
    category: 'appointments',
    recipientRef: `Patient/${params.patientId}`,
    title: 'Appointment rescheduled',
    body,
    linkTarget: `Appointment/${params.appointmentId}`,
    aboutRefs: [`Appointment/${params.appointmentId}`],
    subjectRef: `Patient/${params.patientId}`,
    dedupeBucket: `${params.appointmentId}:${params.whenLabel}`,
  });
}

